import { Show, For, useContext, createSignal, createEffect, on } from 'solid-js';
import { Heading } from '../../ui-components/heading/heading';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Text } from '../../ui-components/text/text';
import { CourseCard, CourseCardData } from '../course-card/course-card';
import { Button } from '../../ui-components/button/button';

import { CertificateCard } from '../certificate-card/certificate-card';
import { ShowpadCodeSubmission } from '../showpad-code-submission/showpad-code-submission';
import { GET_CERTIFICATES_QUERY, GET_PROGRESSING_COURSES_QUERY } from './queries';
import { ShowpadDelayInfoBox } from '../showpad-delay-info-box/showpad-delay-info-box';
import { ErrorCatcher } from '../../tools/error-catcher';

// Static across all sites — editors don't need to upload this per-site.
const CTA_IMAGE_SRC = '/atos-medical-hcp-in-clinic-h.avif';

export const AccountEducationContent = () => {
    const {
        localize,
        createCachedResource,
        userState,
        coursesListingPage,
    } = useContext(AppContext);

    const [ certificatesData, { refetch: refetchCertificates} ] = createCachedResource(GET_CERTIFICATES_QUERY, {}, false);

    const [ coursesData, { refetch: refetchCourseData} ] = createCachedResource(GET_PROGRESSING_COURSES_QUERY, {}, true);

    const [ progressedCourses, setProgressedCourses ] = createSignal<CourseCardData[]>([]);
    const [ certificates, setCertificates ] = createSignal<CourseCardData[]>([]);
    const [ loadingProgress, setLoadingProgress ] = createSignal(true);
    const [ loadingCertificates, setLoadingCertificates ] = createSignal(true);
    const [ certificateDownloadInProgress, setCertificateDownloadInProgress ] = createSignal(false);

    createEffect(() => {
        if (coursesData() && coursesData().getInProgressCourses) {
            setProgressedCourses(coursesData().getInProgressCourses);
            setLoadingProgress(false);
        }
    });

    createEffect(() => {
        if (certificatesData() && certificatesData().getCertificatesByUser) {
            const certs = certificatesData().getCertificatesByUser;
            const sortedByCompletionDate = certs.sort((a: CourseCardData, b: CourseCardData) => {
                return new Date(b.completedDate).getTime() - new Date(a.completedDate).getTime();
            });
            setCertificates(sortedByCompletionDate);
            setLoadingCertificates(false);
        }
    });

    createEffect(
        on(
            () => userState.user?.showpadCode,
            () => {
                // When code changes, we need to refetch the data
                refetchCertificates();
                refetchCourseData();
            },
            { defer: true } // only refetch when showpadCode changes, not initially
        )
    );

    const ExploreCoursesCta = () => (
        <div class="cta-card">
            <div class="cta-card-body">
                <Heading
                    tag="h3"
                    variant="heading-3xl"
                    simpleColor="var(--color-cvi-grey-800)"
                    noBlockSpacing
                >
                    {localize('explore-more-courses', 'Explore our E-Learning courses')}
                </Heading>
                <Button
                    label={localize('view-all-courses', 'View all E-learning courses')}
                    variant="primary"
                    isLink
                    url={coursesListingPage.url}
                />
            </div>
            <div class="cta-card-media">
                <img
                    src={CTA_IMAGE_SRC}
                    alt=""
                    aria-hidden="true"
                />
            </div>
        </div>
    );

    const Certificates = () => (
        <div class="[container-type:inline-size]">
            <Heading tag="h3" variant="heading-2xl" noBlockSpacing>
                {localize('certificates', 'E-Learning Certificates')}
            </Heading>

            <div class="h-cvi-6" />

            <Show when={!loadingCertificates()} fallback={<LoadingText />}>
                <Show when={!certificates() || certificates()?.length === 0}>
                    <Text fontSize="normal">
                        {localize('you-do-not-have-certificates', 'You do not have any certificates')}
                    </Text>
                </Show>

                <Show when={certificates()?.length > 0}>
                    <div class="grid grid-cols-1 @[550px]:grid-cols-2 @[760px]:grid-cols-3 grid-rows-[auto] h-fit gap-x-cvi-8 gap-y-cvi-8">
                        <For each={certificates()}>{(data: CourseCardData) => (
                            <CertificateCard
                                data={data}
                                downloadInProgress={certificateDownloadInProgress()}
                                setDownloadInProgress={(value) => setCertificateDownloadInProgress(value)}
                            />
                        )}</For>
                    </div>
                </Show>
            </Show>
        </div>
    );

    const InProgressCourses = () => (
        <div class="[container-type:inline-size]">
            <Heading tag="h3" variant="heading-2xl" noBlockSpacing>
                {localize('in-progress', 'In progress')}
            </Heading>

            <div class="h-cvi-6" />

            <Show when={!loadingProgress()} fallback={<LoadingText />}>
                <Show when={!progressedCourses() || progressedCourses()?.length === 0}>
                    <Text fontSize="normal">
                        {localize('you-have-no-in-progress-courses', 'You have no E-Learning courses in progress')}
                    </Text>
                </Show>

                <Show when={progressedCourses()?.length > 0}>
                    <div class="grid grid-cols-1 @[550px]:grid-cols-2 @[760px]:grid-cols-3 grid-rows-[auto] h-fit gap-x-cvi-8 gap-y-cvi-8">
                        <For each={progressedCourses()}>{(course) => (
                            <CourseCard course={course} hideFlag={true} />
                        )}</For>
                    </div>
                </Show>
            </Show>
        </div>
    );

    const LoadingText = () => (
        <Text fontSize="normal">
            {localize('loading', 'Loading...')}
        </Text>
    );

    return (
        <ErrorCatcher componentName="AccountEducationContent" logErrorToServer={true}>
            <div class="flex flex-col sm:gap-cvi-11 gap-cvi-9 [container-type:inline-size]">
                <Heading tag="h2" variant="heading-4xl" noBlockSpacing>
                    {localize('e-learning', 'E-learning')}
                </Heading>

                <ShowpadCodeSubmission />

                <ExploreCoursesCta />

                <ShowpadDelayInfoBox
                    text={localize('showpad-progress-delay-info-text', 'Please note that it may take up to an hour for your progress to appear')}
                />

                <InProgressCourses />

                <Certificates />
            </div>
        </ErrorCatcher>
    );
};
